import { useIntl } from 'react-intl'

import { differenceInDays, differenceInMonths, differenceInWeeks, isToday, isTomorrow } from 'date-fns'

import { EpisodeStates } from '../../../models/episodes/Episodes'
import { getModality, MEETING_FORMATS } from '../../../models/session/Session'
import { getDateFromAppointment } from '../../appointments/getDateFromAppointment'
import { numberToOrdinalWord } from '../../common/utils/stringUtils'
import { getProgramDisplayLabel } from '../../programConfigs/programHelpers'

const getTimeVariation = (
  nextAppointment: { startDate: string; startTime: string; timeZone: string } | undefined | null,
): string => {
  if (!nextAppointment || !nextAppointment.startDate || !nextAppointment.startTime || !nextAppointment.timeZone) {
    return 'unscheduled'
  }

  const { startDate, startTime, timeZone } = nextAppointment
  const date = getDateFromAppointment({ startDate, startTime, timeZone })
  const now = new Date()

  if (isToday(date)) return 'today'
  if (isTomorrow(date)) return 'tomorrow'

  const daysDiff = differenceInDays(date, now)
  if (daysDiff <= 7) {
    return `in ${daysDiff} ${daysDiff === 1 ? 'day' : 'days'}`
  }

  const weeksDiff = differenceInWeeks(date, now)
  if (weeksDiff === 1) return 'next week'
  if (weeksDiff <= 4) return `in ${weeksDiff} weeks`

  const monthsDiff = differenceInMonths(date, now)
  if (monthsDiff === 1) return 'next month'
  return `in ${monthsDiff} months`
}

const getEpisodeStateRelatedTitle = (episodeState: EpisodeStates | undefined, clientName: string) => {
  switch (episodeState) {
    case EpisodeStates.COMPLETED:
      return `${clientName} demonstrated readiness to complete care based on progress made on their goals.`
    case EpisodeStates.NOT_SUPPORTED:
      return `${clientName} is looking for other support not covered by Blended Care programs and other Lyra services.`
    case EpisodeStates.CARE_TRANSITION:
      return `${clientName} is not completing care with me and is transitioning to a different Blended Care provider or different care type within Lyra.`
    case EpisodeStates.DROP_OUT:
      return `${clientName} attended at least 1 session and stopped care without having made progress on their goals. This can include clients who have been unresponsive to outreach attempts or declined further sessions.`
    case EpisodeStates.NOT_ELIGIBLE:
      return `${clientName} is losing eligibility for the Blended Care program and other Lyra services or has utilized all their sessions for the year.`
    case EpisodeStates.NOT_INITIATED:
      return `${clientName} booked an appointment but never showed up for any appointments and was not referred to other care.`
    default:
      return undefined
  }
}

export const useGetNextSessionSummaryTitle = (
  episode: { state: EpisodeStates; program_name: string } | undefined | null,
  clientDetails: { first_name: string; preferred_first_name?: string; session_number?: number } | undefined | null,
  nextAppointment:
    | { startDate: string; startTime: string; timeZone: string; meetingFormat: MEETING_FORMATS; treatmentType: string }
    | undefined
    | null,
): string => {
  const intl = useIntl()
  const clientName = clientDetails?.preferred_first_name || clientDetails?.first_name || ''
  const episodeStateRelatedTitle = getEpisodeStateRelatedTitle(episode?.state, clientName)

  if (episodeStateRelatedTitle) {
    return episodeStateRelatedTitle
  }

  if (!nextAppointment || !nextAppointment?.startDate || !nextAppointment?.startTime || !nextAppointment?.timeZone) {
    return `${clientName} has no upcoming sessions`
  }

  const timeVariation = getTimeVariation(nextAppointment)
  const sessionNumberWord = numberToOrdinalWord(clientDetails?.session_number)
  const modality = getModality(nextAppointment.meetingFormat)
  const careType = getProgramDisplayLabel(intl, episode?.program_name, 'sessionSummary')

  return `${clientName}'s ${sessionNumberWord} session of ${modality} ${careType} is ${timeVariation}`
}
